import { ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';
import { AnalysisStateService, RegionSelectionRegion, RegionSelectionSummary, UploadedFileMetadata } from '../../services/analysisState.service';
import { ExecutionDetailItem } from '../../models/execution-details.models';

@Component({
  selector: 'app-topbar-region-info',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './topbar-region-info.component.html',
  styleUrls: ['./topbar-region-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopbarRegionInfoComponent implements OnChanges {
  @Input({ required: true })
  summary: RegionSelectionSummary | null = null;

  isExpanded = false;
  showArgs = false;
  argsPosition: { x: number; y: number } | null = null;
  uploadedFileMetadata$: Observable<UploadedFileMetadata | null>;

  @ViewChild('argsButton') private argsButtonRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('argsPopover')
  set argsPopoverRef(value: ElementRef<HTMLDivElement> | undefined) {
    this.argsPopoverEl = value;
    if (value) {
      // Measure after render so drag boundaries stay in sync with layout.
      setTimeout(() => this.measurePopover(), 0);
    }
  }

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private analysisStateService: AnalysisStateService
  ) {
    this.uploadedFileMetadata$ = this.analysisStateService.uploadedFileMetadata$;
  }

  private argsPopoverEl?: ElementRef<HTMLDivElement>;
  private popoverSize: { width: number; height: number } | null = null;
  private isDraggingArgs = false;
  private dragStartPointer: { x: number; y: number } | null = null;
  private dragStartPosition: { x: number; y: number } | null = null;
  private hasCustomArgsPosition = false;

  trackByRegion(index: number, region: RegionSelectionRegion): string {
    return region.regionId;
  }

  hasRanges(region: RegionSelectionRegion): boolean {
    return !!region.executionRange || !!region.timeVariationRange;
  }

  trackByDetail(index: number, detail: ExecutionDetailItem): string {
    return `${detail.label}:${detail.value}`;
  }

  trackByValue(index: number, value: string): string {
    return `${index}:${value}`;
  }

  get hasExecutionDetails(): boolean {
    const details = this.summary?.executionDetails;
    return !!details && (
      details.summary.length > 0 ||
      details.flags.length > 0 ||
      details.rankValues.length > 0 ||
      details.coreValues.length > 0 ||
      details.inputValues.length > 0 ||
      details.repetitionValues.length > 0 ||
      !!details.command
    );
  }

  get hasRunMatrix(): boolean {
    const details = this.summary?.executionDetails;
    return !!details && (
      details.rankValues.length > 0 ||
      details.coreValues.length > 0 ||
      details.inputValues.length > 0 ||
      details.repetitionValues.length > 0
    );
  }

  get coreLabel(): string {
    return this.summary?.executionDetails.parallelismMode === 'hybrid'
      ? 'Cores / rank'
      : 'Cores';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['summary']) {
      this.closeArgs();
      if (!this.summary) {
        this.isExpanded = false;
        this.argsPosition = null;
        this.hasCustomArgsPosition = false;
      }
    }
  }

  toggle(): void {
    this.isExpanded = !this.isExpanded;
    if (!this.isExpanded) {
      this.closeArgs();
    }
  }

  toggleArgs(event: MouseEvent): void {
    event.stopPropagation();
    if (this.showArgs) {
      this.closeArgs();
    } else {
      this.openArgs();
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node) && this.showArgs) {
      this.closeArgs();
    }
  }

  @HostListener('document:pointermove', ['$event'])
  handlePointerMove(event: PointerEvent): void {
    if (!this.isDraggingArgs || !this.dragStartPointer || !this.dragStartPosition) {
      return;
    }

    const deltaX = event.clientX - this.dragStartPointer.x;
    const deltaY = event.clientY - this.dragStartPointer.y;
    const nextPosition = this.clampPosition({
      x: this.dragStartPosition.x + deltaX,
      y: this.dragStartPosition.y + deltaY
    });

    this.argsPosition = nextPosition;
    this.hasCustomArgsPosition = true;
  }

  @HostListener('document:pointerup')
  handlePointerUp(): void {
    if (!this.isDraggingArgs) {
      return;
    }

    this.isDraggingArgs = false;
    this.dragStartPointer = null;
    this.dragStartPosition = null;
  }

  get primaryTitle(): string {
    if (!this.summary || this.summary.regions.length === 0) {
      return 'No region selected';
    }
    if (this.summary.regions.length === 1) {
      return this.summary.regions[0].label;
    }
    return `${this.summary.regions.length} regions selected`;
  }

  get subtitle(): string | null {
    if (!this.summary) {
      return null;
    }
    const region = this.summary.regions[0];
    if (this.summary.regions.length === 1) {
      return region.filename;
    }
    return 'Clique para ver detalhes';
  }

  startDrag(event: PointerEvent): void {
    if (!this.showArgs) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.isDraggingArgs = true;
    this.dragStartPointer = { x: event.clientX, y: event.clientY };
    const currentPosition = this.argsPosition ?? this.clampPosition({ x: event.clientX, y: event.clientY });
    this.dragStartPosition = { ...currentPosition };
    if (!this.argsPosition) {
      this.argsPosition = currentPosition;
    }
  }

  closeArgs(event?: Event): void {
    event?.stopPropagation();
    this.showArgs = false;
    this.isDraggingArgs = false;
    this.dragStartPointer = null;
    this.dragStartPosition = null;
  }

  private openArgs(): void {
    this.showArgs = true;
    this.ensureInitialArgsPosition();
  }

  private ensureInitialArgsPosition(): void {
    if (this.argsPosition && this.hasCustomArgsPosition) {
      this.argsPosition = this.clampPosition(this.argsPosition);
      return;
    }

    this.setInitialArgsPosition();
  }

  private setInitialArgsPosition(): void {
    const margin = 12;
    if (this.argsButtonRef) {
      const rect = this.argsButtonRef.nativeElement.getBoundingClientRect();
      const initial = { x: rect.left, y: rect.bottom + margin };
      this.argsPosition = this.clampPosition(initial);
    } else {
      this.argsPosition = this.clampPosition({ x: margin, y: margin });
    }
  }

  private measurePopover(): void {
    if (!this.argsPopoverEl) {
      this.popoverSize = null;
      return;
    }

    const rect = this.argsPopoverEl.nativeElement.getBoundingClientRect();
    this.popoverSize = { width: rect.width, height: rect.height };
    if (this.argsPosition) {
      this.argsPosition = this.clampPosition(this.argsPosition);
    }
  }

  private clampPosition(position: { x: number; y: number }): { x: number; y: number } {
    if (typeof window === 'undefined') {
      return position;
    }

    const margin = 12;
    const width = this.popoverSize?.width ?? 520;
    const height = this.popoverSize?.height ?? 560;
    const maxX = Math.max(margin, window.innerWidth - width - margin);
    const maxY = Math.max(margin, window.innerHeight - height - margin);

    return {
      x: Math.min(Math.max(position.x, margin), maxX),
      y: Math.min(Math.max(position.y, margin), maxY)
    };
  }
}
